import _ from 'lodash';
import { axiosWrapper } from '@/shared/api';
import { logError } from '@/shared/lib/apiHelpers';

const ecgDataCache = new Map<string, any>();
const ECG_DATA_CACHE_MAX = 250; // cache limit 250 record

/**
 * Builds the cache key for one ranged ECG byte fetch. Combines the last path segment of the range's dataUrl with its start and stop byte offsets.
 *
 * @param range - Range descriptor carrying dataUrl and a dataRange with start and stop.
 * @returns The cache key identifying this URL and byte range.
 */
function getEcgCacheKey(range: any) {
  // Key ngắn gọn chỉ lấy phần bytes và url cuối
  const urlPart = range.dataUrl.split('/').pop();
  return `${urlPart}:${range.dataRange.start}-${range.dataRange.stop}`;
}

/**
 * Stores a downloaded ECG chunk under a most-recently-used eviction policy. Deletes any existing entry so the key re-enters at the tail, inserts the value, then evicts the oldest key once the map grows past ECG_DATA_CACHE_MAX.
 *
 * @param key - Cache key for the chunk, as built from its URL and byte range.
 * @param value - Downloaded arraybuffer for that range.
 * @returns Nothing.
 */
function setEcgCache(key: string, value: any) {
  if (ecgDataCache.has(key)) ecgDataCache.delete(key);
  ecgDataCache.set(key, value);
  if (ecgDataCache.size > ECG_DATA_CACHE_MAX) {
    const oldestKey = ecgDataCache.keys().next().value;
    ecgDataCache.delete(oldestKey);
  }
}

/**
 * Fetches the ECG byte ranges backing a strip and concatenates them into one signed 16-bit sample array. Serves cached chunks where it can and otherwise issues ranged arraybuffer requests through axiosWrapper, caching each response, then joins them and drops a trailing odd byte; a cancellation is rethrown while any other failure logs and yields an empty array.
 *
 * @param ranges - Byte ranges to fetch, each with a dataUrl and a dataRange.
 * @param querySignature - Signed query string appended to every range URL.
 * @param config - Request config forwarded to axiosWrapper, carrying the cancel key.
 * @returns A promise resolving to the concatenated samples, or an empty array when the download failed.
 */
export const downloadSelectedEcgData = async (
  ranges: any[],
  querySignature: string,
  config: any,
): Promise<Int16Array | any[]> => {
  try {
    if (!ranges.length) return [];
    const promises = ranges.map((range) => {
      const cacheKey = getEcgCacheKey(range);
      if (ecgDataCache.has(cacheKey)) {
        return Promise.resolve(ecgDataCache.get(cacheKey));
      }
      return axiosWrapper(
        `${range.dataUrl}?${querySignature}`,
        {
          responseType: 'arraybuffer',
          headers: {
            Range: `bytes=${range.dataRange.start}-${range.dataRange.stop}`,
          },
        },
        config,
      ).then((x: any) => {
        setEcgCache(cacheKey, x.data);
        return x.data;
      });
    });

    const response = await Promise.all(promises);

    // *: Concat array buffer
    const blob: any[] = [];
    response.forEach((data) => {
      blob.push(data);
    });
    let concatenated = await new Blob(blob).arrayBuffer();
    const { byteLength } = concatenated;
    if (byteLength % 2 !== 0) {
      concatenated = concatenated.slice(0, byteLength - 1);
    }
    return new Int16Array(concatenated);
  } catch (error: any) {
    if (_.includes(error.message, 'canceled')) {
      logError('The user aborted a request', error);
      throw error;
    } else {
      logError('Failed to download selected ECG data: ', error);
    }
    return [];
  }
};
